import React from "react";
import { Link } from "react-router-dom";

import { ColorModeContext } from "../../../../App";
import { styled, useTheme } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { IconButton, Toolbar, Typography } from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import CustomButton from "../../../../helper-components/input/CustomButton";

import { logUserOut } from "../../../../helper-functions/auth/userAuthUtils.js";
import { handleTourGuide } from "../../../../helper-functions/shared/pageUtils.js";

import { getLanguage } from "../../../../config/language/language.js";
import * as Constants from "../../../../config/constants/Constants.js";

const StyledAppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open"
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        marginLeft: Constants.PAGE_DRAWER_WIDTH_PX,
        width: `calc(100% - ${Constants.PAGE_DRAWER_WIDTH_PX}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    // Override media queries injected by theme.mixins.toolbar
    "@media all": {
        minHeight: 64
    }
}));

export default function Appbar(props) {
    const isMobile = props.browser.isMobile;

    const theme = useTheme();
    const colorMode = React.useContext(ColorModeContext);

    return (
        <StyledAppBar position="fixed" open={props.sidebar.openAppDrawer && !isMobile}>
            <StyledToolbar
                sx={{
                    zIndex: isMobile ? 9999 : "initial",
                    pr: "24px" // keep right padding when drawer closed
                }}>
                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="open drawer"
                    onClick={props.sidebar.toggleAppDrawer}
                    sx={{
                        marginRight: "36px",
                        ...(props.sidebar.openAppDrawer && !isMobile && { display: "none" })
                    }}>
                    <MenuIcon />
                </IconButton>

                <Typography component="h1" variant="h5" color="inherit" noWrap sx={{ flexGrow: 1 }}>
                    {getLanguage().TOOLBAR_TITLE}
                </Typography>

                {/* Show the description if the screen is over a certain threshold. */}
                {props.browser.screenWidth > Constants.HIDE_TOOLBAR_DESCRIPTION_PX && (
                    <Typography component="h3" variant="subtitle1" color="inherit" noWrap sx={{ flexGrow: 2 }}>
                        <i>{getLanguage().TOOLBAR_DESCRIPTION}</i>
                    </Typography>
                )}

                <div className="tour-about-2">
                    {/* Tour guide button. */}
                    {props.user.isLoggedIn && (
                        <CustomButton
                            {...props}
                            icon={<HelpOutlineIcon />}
                            disabled={props.browser.tourGuideSteps.length === 0}
                            tooltip="Tour of the page and its features"
                            onClick={() => handleTourGuide(props, true)}
                            color="inherit"
                        />
                    )}

                    {/* Show log in button. */}
                    {!props.user.isLoggedIn && (
                        <Link to="/signin" className="text-link">
                            <IconButton sx={{ ml: 1 }} color="inherit">
                                <LoginIcon />
                            </IconButton>
                        </Link>
                    )}

                    <CustomButton
                        {...props}
                        icon={theme.palette.mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                        tooltip="Change color mode"
                        onClick={colorMode.toggleColorMode}
                        color="inherit"
                        size="medium"
                    />

                    {props.user.isLoggedIn && <CustomButton {...props} icon={<LogoutIcon />} tooltip="Log out" onClick={() => logUserOut(props)} color="inherit" size="medium" />}
                </div>
            </StyledToolbar>
        </StyledAppBar>
    );
}
